import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { ArrowUpRight, X } from 'lucide-react'
import { Button, ContextBusyLoadstone, ContextReadyLoadstone, Tooltip } from '@highlight/ui'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'
import { twMerge } from 'tailwind-merge'
import { BlockStreamBar } from './nested/block-stream-bar'
import { NotificationCard } from './nested/notification-card'
import type { ContextNotification } from './use-context-notification'

export {
  useContextNotification,
  type ContextNotification,
  type ContextNotificationPhase,
} from './use-context-notification'

const variants: Variants = {
  initial: { opacity: 0, y: 8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 8, scale: 0.98 },
}

const transition: Transition = { duration: 0.18, ease: 'easeOut' }

interface NotificationProps {
  notification: ContextNotification
  /** The surface gate: the host decides where the notification may appear. */
  visible: boolean
}

/**
 * The card states, stacked above the floating bar's control row: learning
 * (minimize only, no close) while context generates, ready once it's done.
 * popLayout so an exiting card leaves the flow at once and the row beneath
 * doesn't resize for the length of the exit.
 */
export function ContextNotificationCard({ notification, visible }: NotificationProps) {
  const { phase } = notification
  const show = visible && (phase === 'progress' || phase === 'finished')
  return (
    <AnimatePresence initial={false} mode="popLayout" anchorY="bottom">
      {show && (
        <motion.div
          key={phase}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={transition}
          className="pointer-events-auto w-full"
        >
          {phase === 'finished' ? (
            <ReadyCard onOpen={notification.openHowYouWork} />
          ) : (
            <LearningCard progress={notification.progress} onMinimize={notification.minimize} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/** The minimized learning card: a floating circle in the toolbar row that expands it again. */
export function ContextNotificationPill({ notification, visible }: NotificationProps) {
  return (
    <AnimatePresence initial={false} mode="popLayout" anchorY="bottom">
      {visible && notification.phase === 'minimized' && (
        <motion.div
          key="context-notification-pill"
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={transition}
          className="pointer-events-auto shrink-0"
        >
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  variant="outline"
                  shape="circle"
                  size="xl"
                  aria-label="Show how Highlight is learning how you work"
                  onClick={notification.expand}
                  // Raised off the content it floats over.
                  className={twMerge(FLOATING_FILL_CLASS, 'shadow-lg')}
                >
                  <ContextBusyLoadstone size={20} label="Learning how you work" />
                </Button>
              }
            />
            <Tooltip.Content>Highlight is actively understanding how you work.</Tooltip.Content>
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function LearningCard({ progress, onMinimize }: { progress: number; onMinimize: () => void }) {
  return (
    <NotificationCard
      tint="learning"
      icon={<ContextBusyLoadstone size={20} label="Learning how you work" />}
      title="Learning How You Work"
    >
      <p className="type-prose-sm text-foreground-secondary">
        Highlight is getting to know your work, including who you work with, what you work on, and how you write.
      </p>
      <BlockStreamBar progress={progress} />
      <Button
        variant="ghost"
        shape="square"
        size="sm"
        aria-label="Minimize"
        onClick={onMinimize}
        className="text-foreground-secondary hover:text-foreground absolute right-3 top-3"
      >
        <X aria-hidden className="size-4" />
      </Button>
    </NotificationCard>
  )
}

function ReadyCard({ onOpen }: { onOpen: () => void }) {
  return (
    <NotificationCard
      tint="ready"
      icon={<ContextReadyLoadstone size={20} label="Ready" />}
      title="Up to Speed on How You Work"
    >
      <p className="type-prose-sm text-foreground">
        Highlight is ready to work the way you do, and it&rsquo;ll keep learning as you go.
      </p>
      <Button variant="primary" tone="inverse" size="lg" block onClick={onOpen} right={<ArrowUpRight />}>
        See what Highlight learned
      </Button>
    </NotificationCard>
  )
}
